import { React, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form } from 'react-bootstrap';
import {
  CATEGORY_OPTIONS,
  ALL_CATEGORY_VALUES,
  districtLabel,
  buildAreaSiteCriteria,
} from '../../common/siteFilters';
import styles from './AreaManagementFilter.module.css';

function AreaManagementFilter(props) {
  const { isOpen, setIsOpen, states, districts, filters, setFilters, selections, setSelections } =
    props;
  const [showActive, setShowActive] = useState(!filters.active);
  const [showInactive, setShowInactive] = useState(!filters.inactive);
  const [showStates, setShowStates] = useState(states);
  const [showDistricts, setShowDistricts] = useState(districts);
  const [showCategories, setShowCategories] = useState(ALL_CATEGORY_VALUES);

  // Whenever the modal opens, re-sync the draft checkboxes from the committed
  // selections so a chip removed on the page below is reflected here (and so the
  // draft never drifts from what is actually filtering). No committed selection
  // for a group means every box checked.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setShowStates(selections?.states || states);
    setShowDistricts(selections?.districts || districts);
    setShowCategories(selections?.categories || ALL_CATEGORY_VALUES);
    setShowActive(!filters.active);
    setShowInactive(!filters.inactive);
  }, [isOpen]);

  // When the derived option lists first arrive (data loads async), default any
  // still-untouched group to fully checked.
  useEffect(() => {
    if (!selections?.states) {
      setShowStates(states);
    }
  }, [states]);
  useEffect(() => {
    if (!selections?.districts) {
      setShowDistricts(districts);
    }
  }, [districts]);

  const areAllStatesChecked = () => states.length === showStates.length;
  const areAllDistrictsChecked = () => districts.length === showDistricts.length;
  const areAllCategoriesChecked = () => ALL_CATEGORY_VALUES.length === showCategories.length;

  const toggleValue = (value, list, setList) => {
    if (list.includes(value)) {
      setList(list.filter(item => item !== value));
    } else {
      setList([...list, value]);
    }
  };

  const toggleAll = (allChecked, fullList, setList) => {
    setList(allChecked ? [] : fullList);
  };

  // Reset all filters. This sets each value back to checked.
  const resetFilters = () => {
    setShowActive(true);
    setShowInactive(false);
    setShowStates(states);
    setShowDistricts(districts);
    setShowCategories(ALL_CATEGORY_VALUES);
  };

  const applyFilters = () => {
    // For each value, checked = no need to filter (null predicate); unchecked =
    // provide a predicate that keeps only the checked values.
    const statesFilter = areAllStatesChecked() ? null : area => showStates.includes(area.areaState);
    const activeFilter = showActive ? null : area => !area.active;
    const inactiveFilter = showInactive ? null : area => area.active;
    const siteCriteria = buildAreaSiteCriteria(showDistricts, districts, showCategories);

    setFilters({
      ...filters,
      states: statesFilter,
      active: activeFilter,
      inactive: inactiveFilter,
      siteCriteria,
    });

    // Report the committed selections up so the page can render active-filter
    // chips and re-sync this modal on reopen.
    setSelections({
      states: showStates,
      districts: showDistricts,
      categories: showCategories,
    });

    setIsOpen(false);
  };

  return (
    <Modal show={isOpen} onHide={() => setIsOpen(false)}>
      <Modal.Header closeButton>
        <Modal.Title>Filter</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3" controlId="filter.state">
          <div className={styles['state-label-row']}>
            <Form.Label>
              <b>State</b>
            </Form.Label>
            <Form.Check
              type="checkbox"
              label="All States"
              checked={areAllStatesChecked()}
              onChange={() => toggleAll(areAllStatesChecked(), states, setShowStates)}
            />
          </div>
          <div className={styles.checkboxes}>
            {states.map(state => (
              <Form.Check
                type="checkbox"
                label={state}
                className={styles['form-check']}
                key={state}
                checked={showStates.includes(state)}
                onChange={() => toggleValue(state, showStates, setShowStates)}
              />
            ))}
          </div>
        </Form.Group>
        <Form.Group className="mb-3" controlId="filter.category">
          <div className={styles['state-label-row']}>
            <Form.Label>
              <b>Program Type</b>
            </Form.Label>
            <Form.Check
              type="checkbox"
              label="All Program Types"
              checked={areAllCategoriesChecked()}
              onChange={() =>
                toggleAll(areAllCategoriesChecked(), ALL_CATEGORY_VALUES, setShowCategories)
              }
            />
          </div>
          <div className={styles.checkboxes}>
            {CATEGORY_OPTIONS.map(option => (
              <Form.Check
                type="checkbox"
                label={option.label}
                className={styles['form-check']}
                key={option.value}
                checked={showCategories.includes(option.value)}
                onChange={() => toggleValue(option.value, showCategories, setShowCategories)}
              />
            ))}
          </div>
        </Form.Group>
        <Form.Group className="mb-3" controlId="filter.district">
          <div className={styles['state-label-row']}>
            <Form.Label>
              <b>School District</b>
            </Form.Label>
            <Form.Check
              type="checkbox"
              label="All Districts"
              checked={areAllDistrictsChecked()}
              onChange={() => toggleAll(areAllDistrictsChecked(), districts, setShowDistricts)}
            />
          </div>
          <div className={styles.checkboxes}>
            {districts.map(district => (
              <Form.Check
                type="checkbox"
                label={districtLabel(district)}
                className={styles['form-check']}
                key={district}
                checked={showDistricts.includes(district)}
                onChange={() => toggleValue(district, showDistricts, setShowDistricts)}
              />
            ))}
          </div>
        </Form.Group>
        <Form.Group className="mb-3" controlId="filter.status">
          <Form.Label>
            <b>Area Status</b>
          </Form.Label>
          <div className={styles.checkboxes}>
            <Form.Check
              type="checkbox"
              label="Active"
              className={styles['form-check']}
              checked={showActive}
              onChange={() => setShowActive(!showActive)}
            />
            <Form.Check
              type="checkbox"
              label="Inactive"
              className={styles['form-check']}
              checked={showInactive}
              onChange={() => setShowInactive(!showInactive)}
            />
          </div>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer className={styles['button-row']}>
        <Button onClick={resetFilters}>Reset Filters</Button>
        <Button variant="success" onClick={applyFilters}>
          Apply Filters
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

AreaManagementFilter.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  states: PropTypes.arrayOf(PropTypes.string).isRequired,
  districts: PropTypes.arrayOf(PropTypes.string).isRequired,
  filters: PropTypes.oneOfType([PropTypes.object]).isRequired,
  setFilters: PropTypes.func.isRequired,
  selections: PropTypes.oneOfType([PropTypes.object]),
  setSelections: PropTypes.func.isRequired,
};

AreaManagementFilter.defaultProps = {
  selections: null,
};

export default AreaManagementFilter;
